import { api } from '../../api/client';
// The Sample Prep Batch flow is the TWIN of QC Batch (Analysis).  Jobs,
// Instruments and QC-Types come back in the SAME shapes the QC reader uses, so
// we reuse those DTOs verbatim; only the prep-specific rows (prepMethod /
// functionType / datePrepared / hold-time / taken-sample-unit) and the header
// differ, and those are declared below.
import type {
  QcJob, QcInstrument, QcTypeOption, QcSample, QcSeqRow,
  QcLinkedReagent, QcAvailableReagent, QcStagedReagent,
} from './qcBatch.api';

export type {
  QcJob, QcInstrument, QcTypeOption,
  QcLinkedReagent, QcAvailableReagent, QcStagedReagent,
} from './qcBatch.api';

// ── prep-specific DTOs ───────────────────────────────────────────────────────
// A pending sample for prep = the QC sample plus its prep method (the tier's
// PrepMethod on the queue row).  QcSample already carries `holdTime`.
export interface PrepSample extends QcSample {
  prepMethodOid: string | null;
  prepMethod:    string | null;
  // FunctionType = PrepMethod.PrepType → PrepTypes.SamplePrepType (e.g. "Sample
  // Preparation"); READ-ONLY, resolved by AI_Analysis_PrepBatch_LoadSamples_SP.
  samplePrepType: string | null;
}

// A built prep-sequence row = the QC sequence row plus the prep columns shown
// in the grid (PrepMethod · FunctionType · DatePrepared) and carried for
// persistence (TakenSampleUnit · Multiplier).  QcSeqRow already carries
// `holdTime`, `dateReceived`, `dateCollected`, `client`, `projectId`, etc.
export interface PrepSeqRow extends QcSeqRow {
  prepMethodOid:   string | null;
  prepMethod:      string | null;
  // NTU (turbidity) the analyst enters on the grid; persisted with the batch and
  // used to compute the SamplePrep (Yes/No) column via the ACT rule. Nullable —
  // legacy rows and non-sample rows carry none. (feature 2026-08-14)
  ntu?:            string | number | null;
  // PrepOptions = PrepNameOptions → PrepOptions.PrepName (often null/empty).
  prepOptionsOid:  string | null;
  prepOptions:     string | null;
  // FunctionType = PrepMethod.PrepType → PrepTypes.SamplePrepType (e.g. "Sample Preparation").
  functionTypeOid: string | null;
  functionType:    string | null;
  datePrepared:    string | null;
  takenSampleUnit: string | null;
  multiplier:      number | null;
}

// "Open an existing prep batch" (view mode) — matches the C# PrepBatchHeaderRow.
// jobIds is the ';'-joined display labels; jobOids/instrumentOids are the RAW
// stored id string(s).
export interface PrepHeader {
  prepBatchOid:   string;
  prepBatchId:    string | null;
  createdDate:    string | null;
  createdByName:  string | null;
  testMethodId:   string;
  test:           string | null;
  matrix:         string | null;
  method:         string | null;
  jobIds:         string | null;
  jobOids:        string | null;
  instrumentOids: string | null;
  instrumentName: string | null;
  tier:           number | null;
  sort:           number | null;
  temperature:    string | null;
  humidity:       string | null;
  comments:       string | null;
  status:         number;
  statusText:     string | null;
}
export interface PrepBatchDetail { header: PrepHeader; sequence: PrepSeqRow[] }

// ── request bodies ───────────────────────────────────────────────────────────
export interface PrepLoadBody {
  testMethodId: string;
  // Multi-test Tier-1 batching: all selected tests (supersedes testMethodId when
  // present). Single test = omit / one element (unchanged).
  testMethodIds?: string[];
  jobOids:      string[];
  sort:         number;
  tier:         number;
  // Add Sample: SampleLogIn Oids already in the sequence, omitted from candidates.
  // Absent/empty = the plain Load (no exclusion).
  excludeSampleLogInOids?: string[];
}
export interface PrepBuildSequenceBody {
  testMethodId: string;
  sort:         number;
  tier:         number;
  noRuns:       number;
  samples:      PrepSample[];
}
export interface PrepAddQcBody {
  testMethodId: string;
  parentSort:   number;
  newQcTypeOid: string;
  sequence:     PrepSeqRow[];
}
export interface PrepRemoveQcBody {
  testMethodId: string;
  sequence:     PrepSeqRow[];
  removeSorts:  number[];
}
export interface PrepMoveRowBody {
  testMethodId: string;
  dragSort:     number;
  targetSort:   number;
  sequence:     PrepSeqRow[];
}
export interface PrepAddDilutionBody {
  testMethodId: string;
  parentSort:   number;
  sequence:     PrepSeqRow[];
}
export interface PrepSaveBody {
  testMethodId:   string;
  // Multi-test Tier-1 batching: all selected tests (supersedes testMethodId).
  testMethodIds?: string[];
  matrixId:       string | null;
  methodId:       string | null;
  tier:           number;
  sort:           number;
  jobIds:         string | null;    // ';'-delimited job-id labels
  instrumentOid:  string | null;
  instrumentName: string | null;
  temperature:    string | null;
  humidity:       string | null;
  comments:       string | null;
  sequence:       PrepSeqRow[];
  // Reagents/instruments staged (Others tab) BEFORE the batch existed — persisted
  // atomically with Create (legacy parity, mirrors QcCreateBody). Omitted when none.
  stagedReagents?:        QcStagedReagent[];
  stagedInstrumentOids?:  string[];
}
export interface PrepSaveResult { prepBatchId: string; prepOid: string; seqCount: number; samplesLinked: number }

// One prior prep batch in the "Copy From Previous Batch" picker — matches the C#
// PrepPreviousBatchRow (prep twin of QcPreviousBatch).
export interface PrepPreviousBatch {
  prepOid:         string;
  prepBatchId:     string | null;
  matrix:          string | null;
  statusText:      string | null;
  createdDate:     string | null;
  reagentCount:    number;
  instrumentCount: number;
}

interface Envelope<T> { success: boolean; data?: T; error?: { code: string; message: string } }

const unwrap = <T,>(d: Envelope<T>): T => {
  if (!d.success || d.data === undefined) throw new Error(d.error?.message ?? 'Request failed.');
  return d.data;
};

const BASE = '/api/v1/prep-batches';

export const prepBatchApi = {
  // `tests` (CSV of all selected test methods) is sent for a multi-test Tier-1
  // batch so jobs span every test; a single-test batch omits it (path id alone).
  jobs: (testMethodId: string, sort: number, tier: number, tests?: string[]) =>
    api.get<Envelope<QcJob[]>>(`${BASE}/${encodeURIComponent(testMethodId)}/jobs`,
      { params: { sort, tier, ...(tests && tests.length > 1 ? { tests: tests.join(',') } : {}) } })
      .then(r => unwrap(r.data)),

  instruments: (testMethodId: string, tests?: string[]) =>
    api.get<Envelope<QcInstrument[]>>(`${BASE}/${encodeURIComponent(testMethodId)}/instruments`,
      { params: { ...(tests && tests.length > 1 ? { tests: tests.join(',') } : {}) } })
      .then(r => unwrap(r.data)),

  qcTypes: (testMethodId: string) =>
    api.get<Envelope<QcTypeOption[]>>(`${BASE}/${encodeURIComponent(testMethodId)}/qc-types`)
      .then(r => unwrap(r.data)),

  // Global PrepOptions lookup (Digestion / Extraction) — the editable pre-Sort
  // PrepOptions combo. Not TestMethod-scoped.
  prepOptions: () =>
    api.get<Envelope<{ oid: string; prepName: string | null }[]>>(`${BASE}/prep-options`)
      .then(r => unwrap(r.data)),

  load: (body: PrepLoadBody) =>
    api.post<Envelope<PrepSample[]>>(`${BASE}/load`, body).then(r => unwrap(r.data)),

  buildSequence: (body: PrepBuildSequenceBody) =>
    api.post<Envelope<PrepSeqRow[]>>(`${BASE}/build-sequence`, body).then(r => unwrap(r.data)),

  addQc: (body: PrepAddQcBody) =>
    api.post<Envelope<PrepSeqRow[]>>(`${BASE}/add-qc`, body).then(r => unwrap(r.data)),

  removeQc: (body: PrepRemoveQcBody) =>
    api.post<Envelope<PrepSeqRow[]>>(`${BASE}/remove-qc`, body).then(r => unwrap(r.data)),

  moveRow: (body: PrepMoveRowBody) =>
    api.post<Envelope<PrepSeqRow[]>>(`${BASE}/move-row`, body).then(r => unwrap(r.data)),

  // Add one "<parentCode>R<n>" dilution under the selected row (context menu,
  // post-Sort) — same shared engine the QC form uses.
  addDilution: (body: PrepAddDilutionBody) =>
    api.post<Envelope<PrepSeqRow[]>>(`${BASE}/add-dilution`, body).then(r => unwrap(r.data)),

  save: (body: PrepSaveBody) =>
    api.post<Envelope<PrepSaveResult>>(`${BASE}/save`, body).then(r => unwrap(r.data)),

  getDetail: (prepOid: string) =>
    api.get<Envelope<PrepBatchDetail>>(`${BASE}/${encodeURIComponent(prepOid)}/detail`).then(r => unwrap(r.data)),

  // Persist edits to a saved prep batch (A1 — reconcile-on-Save). Posts the FULL
  // current grid (rows with seqOid = existing, without = added; any stored row
  // whose Oid is absent was removed) + header Temperature/Humidity/Comments/
  // Instrument. Twin of qcBatchApi.updateBatch.
  updateBatch: (prepOid: string, body: {
    temperature: string | null; humidity: string | null; comments: string | null;
    instrumentOid: string | null; instrumentName: string | null; sequence: PrepSeqRow[];
  }) =>
    api.put<Envelope<{ added: number; removed: number; updated: number }>>(
      `${BASE}/${encodeURIComponent(prepOid)}`, body).then(r => unwrap(r.data)),

  // Prior prep batches for the "Copy From Previous Batch" picker (same test method,
  // optionally excluding the current one). Mirrors qcBatchApi.previousBatches.
  previousBatches: (testMethodId: string, excludePrepOid?: string) =>
    api.get<Envelope<PrepPreviousBatch[]>>(`${BASE}/${encodeURIComponent(testMethodId)}/previous-batches`,
      { params: excludePrepOid ? { excludePrepOid } : undefined }).then(r => unwrap(r.data)),

  // Delete a prep batch (returns its samples to the queue). OG SamplePrepBatch
  // _ListView Delete — blocked server-side once the batch is ResultCompleted.
  deleteBatch: (prepOid: string) =>
    api.delete<Envelope<{ prepBatchId: string }>>(`${BASE}/${encodeURIComponent(prepOid)}`).then(r => unwrap(r.data)),

  // "Export To QC Batch" pre-navigation guard (OG btnExportToQCBatch_Execute
  // :139-144). hasQcBatch === true → a linked sample is already in a QC batch, so
  // block with "A QC Batch ID has already been created for this Job ID."
  exportCheck: (prepOid: string) =>
    api.get<Envelope<{ hasQcBatch: boolean }>>(`${BASE}/${encodeURIComponent(prepOid)}/export-check`).then(r => unwrap(r.data)),

  // ── Others tab: Reagents (legacy ReagentLink parity — mirrors qcBatchApi) ────
  linkedReagents: (prepOid: string) =>
    api.get<Envelope<QcLinkedReagent[]>>(`${BASE}/${encodeURIComponent(prepOid)}/reagents`).then(r => unwrap(r.data)),

  /** "Add Reagents" picker catalog, scoped to the batch's test method — twin of the
   *  QC form's call (user report 2026-08-07). */
  availableReagents: (testMethodId?: string | null) =>
    api.get<Envelope<QcAvailableReagent[]>>(`${BASE}/available-reagents`,
      testMethodId ? { params: { testMethodId } } : undefined).then(r => unwrap(r.data)),

  linkReagent: (prepOid: string, reagent: QcAvailableReagent) =>
    api.post<Envelope<{ reagentOid: string }>>(`${BASE}/${encodeURIComponent(prepOid)}/reagents`, {
      reagentId: reagent.reagentID, reagentName: reagent.reagentName, prepType: reagent.prepType,
      expiredDate: reagent.expiryDate, vendorLT: reagent.vendorLT, preparedBy: reagent.preparedBy,
      datePrepared: reagent.datePrepared, dateReceived: reagent.dateReceived, vendorOid: null,
    }).then(r => unwrap(r.data)),

  // The 3rd arg (reagentId) matches the QC signature so the copied Others-tab
  // handlers stay identical; the prep endpoint keys off the junction Oid only.
  unlinkReagent: (prepOid: string, reagentOid: string, _reagentId: string | null) =>
    api.delete<Envelope<{ unlinked: boolean }>>(`${BASE}/${encodeURIComponent(prepOid)}/reagents/${encodeURIComponent(reagentOid)}`).then(r => unwrap(r.data)),

  // ── Others tab: Instruments (legacy InstrumentLink parity — mirrors qcBatchApi) ─
  linkedInstruments: (prepOid: string) =>
    api.get<Envelope<QcInstrument[]>>(`${BASE}/${encodeURIComponent(prepOid)}/instruments-linked`).then(r => unwrap(r.data)),

  /** Add-Instrument picker catalog. `testMethodId` scopes it to the instruments
   *  linked to that test on the legacy "Instruments, Lab Analysis" screen. */
  availableInstruments: (testMethodId?: string | null) =>
    api.get<Envelope<QcInstrument[]>>(`${BASE}/available-instruments`,
      testMethodId ? { params: { testMethodId } } : undefined).then(r => unwrap(r.data)),

  // instrumentName is accepted (QC-signature parity) but the prep endpoint takes
  // the Oid in the POST body, not the URL/query.
  linkInstrument: (prepOid: string, instrumentOid: string, _instrumentName: string | null) =>
    api.post<Envelope<{ linked: boolean }>>(`${BASE}/${encodeURIComponent(prepOid)}/instruments`, { instrumentOid }).then(r => unwrap(r.data)),

  unlinkInstrument: (prepOid: string, instrumentOid: string, _instrumentName: string | null) =>
    api.delete<Envelope<{ unlinked: boolean }>>(`${BASE}/${encodeURIComponent(prepOid)}/instruments/${encodeURIComponent(instrumentOid)}`).then(r => unwrap(r.data)),
};
